/* =========================================================================
 *  FM Buybar — made-to-measure controller  (03-Aug-2026)
 *  ----------------------------------------------------
 *  Drives the closed-state footer rendered by `snippets/fm-buybar-mtm.liquid`
 *  on the calculator templates. The shared `fm-options-sheet.js` cannot do this
 *  job: it resolves a variant from the option axes and reads the price off it,
 *  which on these products yields the £0.83 per-100cm² RATE. Here the state
 *  that matters is whether width AND length are valid, and the price comes from
 *  the calculator's own total.
 *
 *  What it owns
 *    - `body.fm-sheet-active` on mobile, which is what the shared CSS keys off
 *      to hide the legacy sticky bar and its trigger, reserve page padding, and
 *      lift the chat launcher clear of the footer. Reusing that class rather
 *      than inventing a second one keeps all of those rules in one place.
 *    - `--fm-buybar-height`, MEASURED not assumed — slim and selected are
 *      different heights, and a wrong value leaves the page bottom padding
 *      wrong in one of the two modes.
 *    - `data-mode` on the bar, plus the chips and CTA text.
 *
 *  What it deliberately does NOT own
 *    - Opening the picker. Taps are forwarded to the existing hidden
 *      `.pdp-options-trigger`, so `pdp-options-modal.js` keeps sole ownership
 *      of open/close, focus handling and the reparenting.
 *    - Adding to cart. The CTA forwards to `.custom-atc-basket`, so the form's
 *      own validation, part-number lookup and `_fm_qty_mode` handling all still
 *      run exactly as before. Both targets are `display: none` under
 *      `fm-sheet-active`, which does not stop a programmatic click.
 * ========================================================================= */
(function () {
  'use strict';

  var MOBILE_BREAKPOINT = 767;

  function isMobile() {
    /* Guard against a zero width (hidden/background tab): reading 0 as mobile
       would switch the whole footer on for a desktop session. */
    return window.innerWidth > 0 && window.innerWidth <= MOBILE_BREAKPOINT;
  }

  document.addEventListener('DOMContentLoaded', function () {
    var bar = document.querySelector('[data-fm-buybar-mtm]');
    if (!bar) return;

    var chips = bar.querySelector('[data-fm-buybar-chips]');
    var cta = bar.querySelector('[data-fm-buybar-cta]');
    var openers = bar.querySelectorAll('[data-fm-buybar-open]');

    var legacyTrigger = document.querySelector('.pdp-options-trigger');
    var legacyAtc = document.querySelector('.custom-atc-basket');

    /* ---- page-level suppression + measured height ---- */
    function syncActiveClass() {
      document.body.classList.toggle('fm-sheet-active', isMobile());
    }

    function measureHeight() {
      if (!isMobile()) return;
      var h = bar.offsetHeight;
      if (h > 0) {
        document.documentElement.style.setProperty('--fm-buybar-height', h + 'px');
      }
      /* pdp-options-modal.js sizes the gap it leaves beneath the modal panel
         from `--fm-pdp-atc-height`, which it measures off whichever bar is
         pinned at the bottom — us, on this template. Tell it whenever our
         height changes, or the panel floats above a stale offset and a strip of
         page shows through between the two. */
      document.dispatchEvent(new CustomEvent('fm:buybar-resized'));
    }

    /* ---- mode ---- */
    function setMode(mode) {
      if (bar.getAttribute('data-mode') !== mode) {
        bar.setAttribute('data-mode', mode);
      }
      /* Height changes with the mode, so re-measure after the swap. */
      measureHeight();
    }

    /* ---- selected-state content ---- */
    function colourValue() {
      /* The chosen colour, from whichever control this template renders. Falls
         back to the ATC's own variant title so the chip is never empty. */
      var checked = document.querySelector('input[type="radio"][name="option-1"]:checked');
      if (checked && checked.value) return checked.value;
      var selectedLi = document.querySelector('.select_ul li.selected[data-value]');
      if (selectedLi) return selectedLi.getAttribute('data-value');
      return null;
    }

    /* ---- state is READ FROM THE DOM, never cached ------------------------
     * Originally this rendered straight from the `fm:dimensions-state` detail,
     * which meant the chips could only ever change when a DIMENSION changed.
     * Picking a different colour after entering a size left the footer reading
     * the old one ("Dark Grey · 100 × 150cm" beside a Red & Black mat) while
     * the part number, variant id and legacy label had all moved on.
     *
     * Deriving on every render instead means any trigger — a dimension edit, a
     * colour tap, a future control we haven't thought of — produces a correct
     * bar, and there is no cached copy to fall out of step.
     *
     * `.pric_cal[data-total-ex]` doubles as the validity flag: qty_cal_150 sets
     * it only when both dimensions are in range and both reset paths remove it.
     * -------------------------------------------------------------------- */
    function readState() {
      var w = document.querySelector('.width_cal_150');
      var l = document.querySelector('.length_cal_150');
      var priceCal = document.querySelector('.pric_cal');
      var totalEx = priceCal ? priceCal.getAttribute('data-total-ex') : null;
      return {
        width: w ? w.value.trim() : '',
        length: l ? l.value.trim() : '',
        totalEx: totalEx || '',
        valid: !!totalEx
      };
    }

    function render() {
      var state = readState();
      if (!state.valid) {
        setMode('slim');
        return;
      }

      var parts = [];
      var colour = colourValue();
      if (colour) parts.push(colour);
      if (state.width && state.length) {
        /* × not x — matches the size chips everywhere else on the site. */
        parts.push(state.width + ' × ' + state.length + 'cm');
      }
      if (chips) chips.textContent = parts.join(' · ');

      /* CTA price is ALWAYS ex VAT — the sitewide rule is that the headline
         price flips with the VAT toggle but the cart button does not. */
      if (cta) {
        cta.textContent = state.totalEx
          ? 'Add to Basket — ' + state.totalEx
          : 'Add to Basket';
      }

      setMode('selected');
    }

    /* The theme's own option handlers run on the same tick as the events below
       (a swatch label click flips the radio, which then updates the ATC's
       data-variant_id), so read on the NEXT tick or we render the outgoing
       selection. Same deferral pdp-options-modal.js uses for its trigger
       label. */
    function deferredRender() {
      setTimeout(render, 0);
    }

    /* ---- the calculator tells us a dimension changed ---- */
    document.addEventListener('fm:dimensions-state', function () {
      render();
    });

    /* ---- colour changes -------------------------------------------------
     * Mirrors the hook set pdp-options-modal.js uses for its own label, so the
     * footer and the modal's trigger can never disagree about what is selected.
     * The radio `change` is the real signal; the click branches are
     * belt-and-braces for any future non-radio swatch UI.
     * ------------------------------------------------------------------ */
    document.addEventListener('change', function (e) {
      if (e.target && e.target.matches && e.target.matches('input[type="radio"][name^="option-"]')) {
        deferredRender();
      }
    });

    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      if (e.target.closest('.select_ul li') || e.target.closest('.color_swatch_contaner')) {
        deferredRender();
      }
    });

    /* ---- taps ---- */
    for (var i = 0; i < openers.length; i++) {
      openers[i].addEventListener('click', function (e) {
        e.preventDefault();
        if (legacyTrigger) legacyTrigger.click();
      });
    }

    if (cta) {
      cta.addEventListener('click', function (e) {
        e.preventDefault();
        /* Mirrors the disabled state the calculator maintains on the real ATC,
           so an out-of-range size cannot be added from here either. */
        if (!legacyAtc || legacyAtc.hasAttribute('disabled')) return;
        legacyAtc.click();
      });
    }

    /* ---- lifecycle ---- */
    syncActiveClass();
    /* Initial pass, so a page restored with values already in the fields (back
       button, form restoration) opens in the right mode rather than showing the
       slim prompt over a priced mat. */
    render();
    measureHeight();

    window.addEventListener('resize', function () {
      syncActiveClass();
      measureHeight();
    });

    /* The trust row and chips can wrap to a second line once populated, and
       web fonts land after DOMContentLoaded — both change the bar's height. */
    window.addEventListener('load', measureHeight);
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(measureHeight);
    }
  });
})();
